<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/history_generals/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.history_generals')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="ml-12">
    <div class="ml-12">
      <div
        v-if="!showStatistics"
        class="flex justify-center"
        style="padding: 2rem;"
      >
        <Alert
          variant="info"
          :title="$t('pages.history.extraFiltersPlaceholderTitle')"
          :sub-title="$t('pages.history.extraFiltersPlaceholderGeneral')"
        />
      </div>
      <div
        v-if="showStatistics"
        class="ml-12 flex items-center justify-end"
        style="justify-content: flex-end; padding: 1rem 2rem 1rem 1rem;"
      >
        <Switch
          v-model="showDiagram"
          :label="$t('pages.history.showDiagram')"
        />
      </div>
    </div>
    <HistoryGeneralDifferenceStatisticsTable
      v-if="showStatistics && !showDiagram"
      :provider-params="providerParams"
    />
    <HistoryChart
      v-if="showStatistics && showDiagram"
      :raw-data="records"
      data-type="general"
      :visible-columns="visibleColumns"
      :provider-params="providerParams"
    />
    <div
      class="table"
      style="display: flex; flex-direction: column;"
    >
      <data-table
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        class="mb-auto"
        :busy="loadingTable"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
    :per-page-choices="[5, 10, 50, 100, 1000, 10000]"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import { format } from 'date-fns';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import HistoryGeneralDifferenceStatisticsTable from '@utils/HistoryGeneralDifferenceStatisticsTable.vue';
import Switch from '@utils/inputs/Switch.vue';
import Alert from '@utils/Alert.vue';
import { useQueryRef } from '@/hooks/url';
import type {
  HistoryDriver, HistoryGeneral, HydraCollection, Region,
} from '@/entities/Entities';
import HistoryChart from "@utils/chart/HistoryChart.vue";

const showDiagram = ref(false);

const page = useQueryRef<number>('page', 1);
const perPage = useQueryRef<number>('perPage', 10);
const filter = useQueryRef<any>('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'recordingDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.history.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'recordingDate',
    title: t('pages.history.recordingDate'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.recordingDate'),
      },
    },
  },
  {
    key: 'exists[region]',
    title: t('pages.history.overall'),
    group: t('pages.history.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: HistoryDriver) => ((item.region === null) ? t('yes') : t('no')),
    filter: {
      type: 'dropdown',
      group: t('pages.history.filterGroups.general'),
      options: {
        options: [
          { value: false, label: t('yes') },
          { value: true, label: t('no') },
        ],
        label: t('pages.history.overall'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('campaign.region'),
    group: t('pages.history.fieldGroups.general'),
    sortable: true,
    visible: false,
    computed: (item: HistoryDriver) => ((item?.region === null) ? t('pages.history.overall') : item.region?.name) ?? '',
    filter: {
      key: 'region',
      type: 'tags',
      group: t('pages.history.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        placeholder: t('select2', { what: t('campaign.region2') }),
      },
    },
  },
  {
    key: 'countPastCampaigns',
    title: t('pages.history.countPastCampaigns'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countPastCampaigns'),
      },
    },
  },
  {
    key: 'countPotentialCampaignSlots',
    title: t('pages.history.countPotentialCampaignSlots'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countPotentialCampaignSlots'),
      },
    },
  },
  {
    key: 'countUsedCampaignSlots',
    title: t('pages.history.countUsedCampaignSlots'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countUsedCampaignSlots'),
      },
    },
  },
  {
    key: 'countActiveCampaigns',
    title: t('pages.history.countActiveCampaigns'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countActiveCampaigns'),
      },
    },
  },
  {
    key: 'countActiveLicenses',
    title: t('pages.history.countActiveLicenses'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countActiveLicenses'),
      },
    },
  },
  {
    key: 'countPastLicenses',
    title: t('pages.history.countPastLicenses'),
    sortable: true,
    visible: true,
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.countPastLicenses'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
} = useProvider<HydraCollection<HistoryGeneral>>('/history_generals', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

// eslint-disable-next-line max-len
const showStatistics = computed(() =>
  ((providerParams?.filter?.region?.length > 0 || (providerParams?.filter?.['exists[region]'] === false)))
  && !(providerParams?.filter?.region?.length > 0 && providerParams?.filter?.['exists[region]'] === false));
</script>
